import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';

import { FlightDeckEvent, MigrationEventsResponse, getMigrationEvents } from '../api/migrations';
import RelativeTime from './RelativeTime';
import { AbLink, EmptyState, SectionPanel } from './abPrimitives';
import { QueryGate } from './routePrimitives';
import { IDENTIFIER, STATE, STROKE, TEXT, type StateName } from '../theme/tokens';

// Campaign-scoped activity (FD-492).
//
// ── FD-ADR-043 D4 IS NARROWED HERE, NOT OVERTURNED, AND THE DIFFERENCE IS THE
//    WHOLE REASON THIS COMPONENT IS ALLOWED TO EXIST ─────────────────────────
//
// That decision omitted "recent activity / group activity feeds" from the
// console because `FlightDeckEvents` is partitioned by `jobId`, so there is no
// partition that spans campaigns. That is still true, and a DEPLOYMENT-WIDE feed
// is still impossible: building one would mean a fan-out across every device of
// every campaign, which is exactly the read pattern ADR-003 and FD-ADR-030 §4
// refuse on a landing page.
//
// A CAMPAIGN-SCOPED feed is a different question with a different answer.
// Thirteen campaign lifecycle events are already written with
// `-JobId $migrationID` — `migrationCreated`, `devices.assigned`,
// `devices.removed`, `identitymapping.updated`, `identity-mapping.added-late`,
// `bprt.mint.session.started`, `bprt.wave.activated`, `licensing.checkout`,
// `ppkg.build`, `readiness.override`, `canary.released`, `bprt.wave.completed`,
// `wave.aborted` — so the campaign has a partition of its own, and reading it is
// ONE O(1) partition query. No new endpoint, no new parameter, no backend
// change: it is the call the event log already makes, with the campaign id where
// a device's job id normally goes.
//
// If a future change wants a feed ACROSS campaigns, it wants the thing D4
// refused. This component is not the precedent for it.
//
// ── NOTHING IS EVER DROPPED (the FD-197 rule) ──────────────────────────────
//
// An event subtype this component has not been taught renders VERBATIM. A new
// event type added to the Manager is precisely the one most worth seeing, and a
// label map that silently skipped its own misses would make it invisible — which
// is what a *faulted* device looked like in the device grid until FD-197. The
// same goes for `eventStatus`: an unrecognised value is rendered as it arrived,
// on a neutral dot, rather than coerced into pass or fail.

/** How many rows the panel shows before deferring to the full event log. */
const VISIBLE = 8;

/**
 * The thirteen campaign lifecycle subtypes, in the operator's words.
 *
 * A MAP, NOT A FILTER. Absence from this table changes how a row is LABELLED and
 * never whether it appears — see the FD-197 note above.
 */
const EVENT_LABELS: Record<string, string> = {
  migrationCreated: 'Campaign created',
  'devices.assigned': 'Devices assigned',
  'devices.removed': 'Devices removed',
  'identitymapping.updated': 'Identity mappings updated',
  'identity-mapping.added-late': 'Identity mapping added during an active wave',
  'bprt.mint.session.started': 'Wave sign-in started',
  'bprt.wave.activated': 'Wave activated',
  'licensing.checkout': 'Migration licences reserved',
  'ppkg.build': 'Provisioning package built',
  'readiness.override': 'Readiness override recorded',
  'canary.released': 'Canary hold released',
  'bprt.wave.completed': 'Wave completed',
  'wave.aborted': 'Wave aborted',
};

/**
 * `eventStatus` → a dot colour.
 *
 * The EventHelper v1.2 vocabulary, and the FLUENT_UI_ADDENDUM split it carries:
 * amber means a human must act, red means something broke. An unknown value
 * lands on `neutral` and is still rendered verbatim beside the label, so an
 * operator sees the word the emitter actually wrote.
 */
function statusTone(status: string): StateName {
  switch (status.trim().toLowerCase()) {
    case 'success':
    case 'pass':
      return 'success';
    case 'error':
    case 'fail':
      return 'critical';
    case 'warning':
      return 'warning';
    case 'pending':
      return 'progress';
    default:
      return 'neutral';
  }
}

function isPlainSuccess(status: string): boolean {
  const s = status.trim().toLowerCase();
  return s === 'success' || s === 'pass';
}

export interface CampaignActivityProps {
  migrationId: string;
}

export default function CampaignActivity({ migrationId }: CampaignActivityProps) {
  // THE SAME QUERY KEY SHAPE `EventLogPage` USES (`['events', migrationId,
  // jobId]`), with the campaign id as the jobId. Following the log's key rather
  // than inventing a second one means the panel and the full log share one cache
  // entry when an operator moves between them, instead of issuing the identical
  // partition query twice.
  const query = useQuery<MigrationEventsResponse>({
    queryKey: ['events', migrationId, migrationId],
    queryFn: () => getMigrationEvents(migrationId, migrationId),
    enabled: Boolean(migrationId),
  });

  // Newest first. ISO-8601 sorts correctly as a string, so no Date parsing and
  // no timezone drift — the same reasoning `EventLogPage` records.
  const events = useMemo<FlightDeckEvent[]>(
    () =>
      [...(query.data?.events ?? [])].sort((a, b) =>
        (b.timeStamp ?? '').localeCompare(a.timeStamp ?? ''),
      ),
    [query.data],
  );

  const logPath = `/migrations/${encodeURIComponent(migrationId)}/events?jobId=${encodeURIComponent(
    migrationId,
  )}`;
  const hidden = Math.max(events.length - VISIBLE, 0);

  return (
    <SectionPanel
      title="Recent activity"
      description="Campaign lifecycle events, newest first."
      action={<AbLink to={logPath}>View event log →</AbLink>}
      bodyClassName="px-5 pb-4"
    >
      <QueryGate isLoading={query.isLoading} error={query.error} label="campaign activity">
        {events.length === 0 ? (
          <EmptyState
            title="No campaign activity yet"
            detail="Campaign events — devices assigned, identities mapped, the wave activated — appear here as they happen."
          />
        ) : (
          <ul className="m-0 flex list-none flex-col p-0">
            {events.slice(0, VISIBLE).map((event) => (
              <ActivityRow key={`${event.rowKey}-${event.eventId}`} event={event} />
            ))}
            {hidden > 0 && (
              <li className="pt-3 text-[12px]" style={{ color: TEXT.secondary }}>
                <ScrollText
                  size={13}
                  strokeWidth={1.5}
                  className="mr-1.5 inline-block align-[-2px]"
                />
                {hidden} earlier {hidden === 1 ? 'event' : 'events'} in the{' '}
                <AbLink to={logPath}>event log</AbLink>.
              </li>
            )}
          </ul>
        )}
      </QueryGate>
    </SectionPanel>
  );
}

function ActivityRow({ event }: { event: FlightDeckEvent }) {
  const subType = event.eventSubType ?? '';
  const label = EVENT_LABELS[subType] ?? subType;
  const status = String(event.eventStatus ?? '');
  const tone = STATE[statusTone(status)];

  return (
    <li
      className="flex items-baseline justify-between gap-4 border-t py-2.5 first:border-t-0 first:pt-0"
      style={{ borderColor: STROKE.subtle }}
    >
      <span className="flex min-w-0 items-baseline gap-2.5">
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 translate-y-[-2px] rounded-full"
          // A dot may take the glyph hex: it is a graphic, not text.
          style={{ backgroundColor: tone.glyph }}
        />
        <span className="min-w-0">
          <span className="block text-[13px]" style={{ color: TEXT.primary }}>
            {label}
          </span>
          {/* The status word only earns a line when it is not the ordinary one.
              Printing "success" under every row would bury the one row that says
              something else — and the value is rendered as the emitter wrote it,
              never mapped to a friendlier word. */}
          {status && !isPlainSuccess(status) && (
            <span className="mt-0.5 block text-[11px]" style={{ ...IDENTIFIER, color: tone.text }}>
              {status}
            </span>
          )}
        </span>
      </span>
      <span className="shrink-0 text-[11px]" style={{ color: TEXT.secondary }}>
        <RelativeTime iso={event.timeStamp} />
      </span>
    </li>
  );
}
